import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  FileText, TrendingUp, CheckCircle2, Users2,
  Target, Clock, AlertCircle, ArrowUpRight, ArrowDownRight,
  BarChart2, Trophy, Zap, DollarSign, Calendar,
  ChevronUp, ChevronDown, Minus, RefreshCw, Download, Globe,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  OPEN:   { color: 'bg-blue-100 text-blue-700 border-blue-200',   dot: 'bg-blue-500',   label: 'Open' },
  CLOSED: { color: 'bg-gray-100 text-gray-600 border-gray-200',   dot: 'bg-gray-400',   label: 'Closed' },
  DRAFT:  { color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-400',  label: 'Draft' },
};

const fmtBudget = (v, cur) => {
  if (!v) return '—';
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
  return String(v);
};

const BudgetBar = ({ min, max, avg }) => {
  if (!max) return <span className="text-xs text-gray-400">No proposals</span>;
  const hitRate = max > 0 ? avg / max : 0;
  const isOver  = hitRate > 1.1;
  const isClose = hitRate >= 0.9 && hitRate <= 1.1;
  const isUnder = hitRate < 0.9;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isOver ? 'bg-red-400' : isClose ? 'bg-emerald-400' : 'bg-amber-400'}`}
          style={{ width: `${Math.min(hitRate * 100, 100)}%` }}
        />
      </div>
      <span className={`text-xs font-bold ${isOver ? 'text-red-600' : isClose ? 'text-emerald-600' : 'text-amber-600'}`}>
        {isOver ? <ArrowUpRight className="w-3 h-3 inline" /> : isClose ? <CheckCircle2 className="w-3 h-3 inline" /> : <ArrowDownRight className="w-3 h-3 inline" />}
        {' '}{Math.round(hitRate * 100)}%
      </span>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, color, trend }) => (
  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${trend > 0 ? 'bg-emerald-50 text-emerald-700' : trend < 0 ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-500'}`}>
          {trend > 0 ? <ChevronUp className="w-3 h-3" /> : trend < 0 ? <ChevronDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
          {trend > 0 ? '+' : ''}{trend}%
        </div>
      )}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-bold text-gray-800 mb-1 max-w-[160px] truncate">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

export default function ExhibitorAnalytics() {
  const { token } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [sortKey, setSortKey] = useState('total_proposals');
  const [sortDir, setSortDir] = useState('desc');
  const [daysFilter, setDaysFilter] = useState(null); // null = all time

  const fetchData = async (days = daysFilter) => {
    setLoading(true); setError(null);
    try {
      const params = days ? { days } : {};
      const res = await axios.get(`${API}/exhibitor/brief-analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (e) {
      setError('Failed to load analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchData(); }, [token]);

  const handleFilterChange = (days) => {
    setDaysFilter(days);
    fetchData(days);
  };

  // CSV Export
  const exportCSV = () => {
    if (!data?.briefs?.length) return;
    const headers = ['Title', 'Status', 'Total Proposals', 'Accepted', 'Pending', 'Declined', 'Avg Proposal', 'Budget Max', 'Currency', 'Days Active', 'Deadline'];
    const rows = data.briefs.map(b => [
      `"${b.title}"`, b.status, b.total_proposals, b.accepted_proposals,
      b.pending_proposals, b.declined_proposals, b.avg_proposal_amount,
      b.target_budget_max, b.currency, b.days_active, b.deadline_at || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `brief-analytics-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      <p className="text-sm text-gray-500">Loading analytics...</p>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <AlertCircle className="w-10 h-10 text-red-400" />
      <p className="text-sm text-red-600">{error}</p>
      <button onClick={fetchData} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
        <RefreshCw className="w-3.5 h-3.5" /> Retry
      </button>
    </div>
  );

  const { summary, platform_benchmark: pb, briefs } = data || { summary: {}, platform_benchmark: {}, briefs: [] };

  const DATE_FILTERS = [
    { label: 'All Time', days: null },
    { label: '90 Days',  days: 90 },
    { label: '30 Days',  days: 30 },
  ];

  // Chart data
  const proposalChartData = briefs
    .filter(b => b.status !== 'DRAFT')
    .slice(0, 8)
    .map(b => ({
      name: b.title.split(' — ')[0].slice(0, 18),
      proposals: b.total_proposals,
      accepted: b.accepted_proposals,
      pending: b.pending_proposals,
    }));

  const budgetChartData = briefs
    .filter(b => b.avg_proposal_amount > 0 && b.target_budget_max > 0)
    .slice(0, 6)
    .map(b => ({
      name: b.title.split(' — ')[0].slice(0, 14),
      target: b.target_budget_max,
      avg_proposal: b.avg_proposal_amount,
      min_proposal: b.min_proposal_amount,
    }));

  // Sorted table
  const sortedBriefs = [...briefs].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  // Insights
  const topBrief = briefs.reduce((best, b) => (!best || b.total_proposals > best.total_proposals) ? b : best, null);
  const fastBrief = briefs.filter(b => b.total_proposals > 0).reduce((best, b) => (!best || b.days_active < best.days_active) ? b : best, null);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <Minus className="w-3 h-3 text-gray-300 inline ml-1" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-500 inline ml-1" /> : <ChevronDown className="w-3 h-3 text-blue-500 inline ml-1" />;
  };

  const PROPOSAL_COLORS = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe', '#eff6ff'];

  return (
    <div className="space-y-6" data-testid="exhibitor-analytics">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-blue-600" />
            Brief Performance
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Analyze your brief activity and proposal performance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Filter */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-0.5">
            {DATE_FILTERS.map(f => (
              <button key={f.label}
                onClick={() => handleFilterChange(f.days)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${daysFilter === f.days ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`filter-${f.label.toLowerCase().replace(' ', '-')}`}>
                {f.label}
              </button>
            ))}
          </div>
          {/* CSV Export */}
          <button onClick={exportCSV} disabled={!data?.briefs?.length}
            data-testid="export-csv"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-40">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={() => fetchData()} data-testid="refresh-analytics"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={FileText}      label="Total Briefs"          value={summary.total_briefs ?? 0}           sub={`${summary.briefs_with_proposals ?? 0} received proposals`} color="bg-blue-500" />
        <KpiCard icon={Users2}        label="Total Proposals"       value={summary.total_proposals ?? 0}         sub={`~${summary.avg_proposals_per_brief ?? 0} per brief`}        color="bg-indigo-500" />
        <KpiCard icon={TrendingUp}    label="Avg Proposals / Brief" value={summary.avg_proposals_per_brief ?? 0} sub="platform avg below"                                          color="bg-violet-500" />
        <KpiCard icon={CheckCircle2}  label="Acceptance Rate"       value={`${summary.acceptance_rate ?? 0}%`}  sub={`${summary.total_accepted ?? 0} proposals accepted`}        color="bg-emerald-500" />
      </div>

      {/* Benchmark vs Platform */}
      {pb && (pb.avg_proposals_per_brief > 0 || pb.acceptance_rate > 0) && (
        <Card className="border-0 shadow-sm bg-gradient-to-r from-slate-50 to-blue-50">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-4 h-4 text-blue-500" />
              <h3 className="text-sm font-bold text-gray-800">Platform Benchmark</h3>
              <Badge className="ml-auto text-[10px] bg-blue-50 text-blue-600 border-blue-200">vs {pb.total_briefs} platform briefs</Badge>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Avg proposals */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">Avg Proposals / Brief</span>
                  <div className="flex items-center gap-1">
                    {(summary.avg_proposals_per_brief ?? 0) >= pb.avg_proposals_per_brief
                      ? <><ArrowUpRight className="w-3 h-3 text-emerald-500" /><span className="text-emerald-600 font-bold">Above avg</span></>
                      : <><ArrowDownRight className="w-3 h-3 text-amber-500" /><span className="text-amber-600 font-bold">Below avg</span></>}
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-400 mb-1">You</div>
                    <div className="h-6 bg-blue-100 rounded-md flex items-center px-2 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-blue-500 rounded-md transition-all"
                        style={{ width: `${Math.min((summary.avg_proposals_per_brief || 0) / Math.max(pb.avg_proposals_per_brief, 1) * 100, 100)}%` }} />
                      <span className="relative z-10 text-xs font-bold text-white mix-blend-difference">{summary.avg_proposals_per_brief ?? 0}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-400 mb-1">Platform</div>
                    <div className="h-6 bg-gray-100 rounded-md flex items-center px-2">
                      <span className="text-xs font-bold text-gray-600">{pb.avg_proposals_per_brief}</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Acceptance rate */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-medium">Acceptance Rate</span>
                  <div className="flex items-center gap-1">
                    {(summary.acceptance_rate ?? 0) >= pb.acceptance_rate
                      ? <><ArrowUpRight className="w-3 h-3 text-emerald-500" /><span className="text-emerald-600 font-bold">Above avg</span></>
                      : <><ArrowDownRight className="w-3 h-3 text-amber-500" /><span className="text-amber-600 font-bold">Below avg</span></>}
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-400 mb-1">You</div>
                    <div className="h-6 bg-emerald-100 rounded-md flex items-center px-2 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded-md transition-all"
                        style={{ width: `${Math.min((summary.acceptance_rate || 0) / Math.max(pb.acceptance_rate, 1) * 100, 100)}%` }} />
                      <span className="relative z-10 text-xs font-bold text-white mix-blend-difference">{summary.acceptance_rate ?? 0}%</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] text-gray-400 mb-1">Platform</div>
                    <div className="h-6 bg-gray-100 rounded-md flex items-center px-2">
                      <span className="text-xs font-bold text-gray-600">{pb.acceptance_rate}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights Banner */}
      {(topBrief || fastBrief) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {topBrief && topBrief.total_proposals > 0 && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-blue-800 uppercase tracking-wide">Most Competitive Brief</p>
                <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{topBrief.title}</p>
                <p className="text-xs text-blue-600 mt-1">{topBrief.total_proposals} proposals — {topBrief.accepted_proposals} accepted</p>
              </div>
            </div>
          )}
          {fastBrief && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
              <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Fastest Response</p>
                <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{fastBrief.title}</p>
                <p className="text-xs text-emerald-600 mt-1">First proposal in {fastBrief.days_active} days</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Charts Row */}
      {(proposalChartData.length > 0 || budgetChartData.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Proposals per Brief */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <Users2 className="w-4 h-4 text-blue-500" />Proposals Received per Brief
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              {proposalChartData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-xs text-gray-400">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={proposalChartData} layout="vertical" margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="accepted"  stackId="a" fill="#10b981" name="Accepted" radius={[0,0,0,0]} />
                    <Bar dataKey="pending"   stackId="a" fill="#3b82f6" name="Pending"  radius={[0,0,0,0]} />
                    <Bar dataKey="proposals" stackId="b" fill="#e2e8f0" name="Total"    radius={[0,3,3,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="flex items-center gap-4 px-4 mt-2">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /><span className="text-xs text-gray-500">Accepted</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-blue-500" /><span className="text-xs text-gray-500">Pending</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-slate-200" /><span className="text-xs text-gray-500">Total</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Budget vs Proposals */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-violet-500" />Target Budget vs Avg Proposal
              </CardTitle>
            </CardHeader>
            <CardContent className="px-2 pb-4">
              {budgetChartData.length === 0 ? (
                <div className="h-48 flex items-center justify-center text-xs text-gray-400">No proposals with budget data</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={budgetChartData} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false}
                      tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                    <Tooltip content={<CustomTooltip />} formatter={(v) => v.toLocaleString()} />
                    <Bar dataKey="target"       fill="#ddd6fe" name="Budget Max" radius={[4,4,0,0]} />
                    <Bar dataKey="avg_proposal" fill="#7c3aed" name="Avg Proposal" radius={[4,4,0,0]} />
                    <Bar dataKey="min_proposal" fill="#a78bfa" name="Min Proposal" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="flex items-center gap-4 px-4 mt-2">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-violet-200" /><span className="text-xs text-gray-500">Budget Max</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-violet-700" /><span className="text-xs text-gray-500">Avg Proposal</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-violet-400" /><span className="text-xs text-gray-500">Min Proposal</span></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Table */}
      <Card className="border-0 shadow-sm" data-testid="analytics-table">
        <CardHeader className="pb-3 pt-4 px-5">
          <CardTitle className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-500" />Brief Performance Detail
            <Badge className="ml-auto text-xs bg-blue-50 text-blue-700 border-blue-200 font-medium">
              {briefs.length} briefs
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {briefs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
              <FileText className="w-10 h-10" />
              <p className="text-sm font-medium">No briefs yet</p>
              <p className="text-xs">Publish your first brief to start tracking performance</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[220px]">Brief</th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort('total_proposals')}>
                      Proposals <SortIcon col="total_proposals" />
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort('avg_proposal_amount')}>
                      Avg Proposed <SortIcon col="avg_proposal_amount" />
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                      Budget Coverage
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden lg:table-cell cursor-pointer hover:text-blue-600"
                      onClick={() => handleSort('days_active')}>
                      Days Active <SortIcon col="days_active" />
                    </th>
                    <th className="text-center py-3 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell">
                      Deadline
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBriefs.map((b, idx) => {
                    const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.DRAFT;
                    const daysLeft = b.days_to_deadline;
                    const isUrgent = daysLeft !== null && daysLeft <= 7 && daysLeft >= 0;
                    const isPast   = daysLeft !== null && daysLeft < 0;
                    return (
                      <tr key={b.id} data-testid={`brief-row-${b.id}`}
                        className="border-b border-gray-50 hover:bg-blue-50/40 transition-colors">
                        <td className="py-3.5 px-5">
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`} />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-800 text-sm leading-tight truncate max-w-[200px]">{b.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={`text-[10px] px-1.5 py-0 border ${sc.color}`}>{sc.label}</Badge>
                                <span className="text-[10px] text-gray-400">{b.currency}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-3 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-base font-bold text-gray-900">{b.total_proposals}</span>
                            {b.total_proposals > 0 && (
                              <div className="flex items-center gap-1 text-[10px]">
                                {b.accepted_proposals > 0 && <span className="text-emerald-600 font-semibold">{b.accepted_proposals}✓</span>}
                                {b.pending_proposals > 0 && <span className="text-blue-500">{b.pending_proposals}◉</span>}
                                {b.declined_proposals > 0 && <span className="text-gray-400">{b.declined_proposals}✗</span>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-3 text-center hidden md:table-cell">
                          {b.avg_proposal_amount > 0 ? (
                            <div className="flex flex-col items-center">
                              <span className="font-semibold text-gray-800">
                                {fmtBudget(b.avg_proposal_amount, b.currency)} {b.currency}
                              </span>
                              {b.min_proposal_amount !== b.avg_proposal_amount && (
                                <span className="text-[10px] text-gray-400">
                                  {fmtBudget(b.min_proposal_amount)} – {fmtBudget(b.max_proposal_amount)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="py-3.5 px-3 hidden lg:table-cell">
                          <div className="min-w-[100px]">
                            {b.target_budget_max > 0 ? (
                              <>
                                <BudgetBar min={b.target_budget_min} max={b.target_budget_max} avg={b.avg_proposal_amount} />
                                <div className="text-[10px] text-gray-400 mt-0.5 text-center">
                                  Target: {fmtBudget(b.target_budget_min)}–{fmtBudget(b.target_budget_max)} {b.currency}
                                </div>
                              </>
                            ) : (
                              <span className="text-xs text-gray-400">No budget set</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-3 text-center hidden lg:table-cell">
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm font-medium text-gray-700">{b.days_active}d</span>
                          </div>
                        </td>
                        <td className="py-3.5 px-3 text-center hidden xl:table-cell">
                          {b.deadline_at ? (
                            <div className={`text-xs font-medium px-2 py-1 rounded-full inline-flex items-center gap-1
                              ${isPast ? 'bg-gray-100 text-gray-500' : isUrgent ? 'bg-red-50 text-red-600' : 'bg-slate-50 text-slate-600'}`}>
                              <Calendar className="w-3 h-3" />
                              {isPast ? `${Math.abs(daysLeft)}d ago` : daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
